import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SupervisedPage.css";
import { apiRequest, logout } from "../utils/api";

function SupervisedLearning() {
    const [columns, setColumns] = useState([]);
    const [allColumns, setAllColumns] = useState([]);
    const [noNumericalColumns, setNoNumericalColumns] = useState(false);
    const [targetVariable, setTargetVariable] = useState("");
    const [loading, setLoading] = useState(false);
    const [time, setTime] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        fetchColumns();
        const interval = setInterval(() => {
            const currentTime = new Date();
            const hours = currentTime.getHours() % 12 || 12;
            const minutes = currentTime.getMinutes().toString().padStart(2, "0");
            const seconds = currentTime.getSeconds().toString().padStart(2, "0");
            const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
            setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
            navigate('/login');
        }
    };

    const fetchColumns = async () => {
        setLoading(true);
        try {
            const [numData, allData] = await Promise.all([
                apiRequest("/api/dataset/numerical-columns"),
                apiRequest("/api/dataset/columns")
            ]);
            if (numData.status === "success") {
                if (numData.columns.length === 0) {
                    setNoNumericalColumns(true);
                    setAllColumns(allData?.columns || []);
                } else {
                    setColumns(numData.columns);
                }
            } else {
                console.error("Error fetching columns:", numData.message);
            }
        } catch (error) {
            console.error("Error fetching columns:", error);
        }
        setLoading(false);
    };

    const handleSelectTarget = (column) => {
        setTargetVariable(column);
        localStorage.setItem("selectedTarget", column);
        navigate("/algorithm-selection");
    };

    return (
        <div className="supervised-learning">
            {/* Top Navigation Bar */}
            <div className="top-nav">
                <div className="nav-content">
                    <div className="nav-left-group">
                        <div className="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
                            <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
                        </div>
                        <button className="back-nav-button" onClick={() => navigate("/analysis-selection")}>
                            Back
                        </button>
                    </div>
                    <div className="nav-right">
                        <div className="digital-clock">{time}</div>
                        <button onClick={handleLogout} className="logout-button">Logout</button>
                    </div>
                </div>
            </div>

            {/* Header Section */}
            <div className="page-header">
                <div className="header-content">
                    <h1>Supervised Learning</h1>
                    <p className="header-subtitle">Select the target variable you want to predict or classify</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="page-content">
                <div className="info-box">
                    <h3>What is a Target Variable?</h3>
                    <p>The target variable is the outcome you want to predict. For example, if you're predicting patient readmission, "readmission_status" would be your target variable.</p>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading columns...</p>
                    </div>
                ) : noNumericalColumns ? (
                    <div className="empty-state">
                        <p><strong>No numerical columns found in your dataset.</strong></p>
                        <p>
                            Supervised learning needs at least one numerical column as the target variable
                            to predict or classify, for example, a column containing values like{' '}
                            <em>0/1, age, score, price</em>, or any other number.
                        </p>
                        {allColumns.length > 0 && (
                            <div style={{ margin: '12px 0', textAlign: 'left', display: 'inline-block' }}>
                                <p style={{ marginBottom: '6px' }}>
                                    <strong>Columns found in your dataset ({allColumns.length}):</strong>
                                </p>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#555' }}>
                                    {allColumns.map((col, i) => (
                                        <li key={i}>{col}</li>
                                    ))}
                                </ul>
                                <p style={{ marginTop: '10px', color: '#888', fontSize: '13px' }}>
                                    All of these appear to be text/categorical. Please upload a dataset that contains at least one numeric column.
                                </p>
                            </div>
                        )}
                        <button onClick={() => navigate("/auto-ml")} className="primary-button">
                            Upload New Dataset
                        </button>
                    </div>
                ) : columns.length === 0 ? (
                    <div className="empty-state">
                        <p>No columns available. Please upload a dataset first.</p>
                        <button onClick={() => navigate("/auto-ml")} className="primary-button">
                            Upload Dataset
                        </button>
                    </div>
                ) : (
                    <>
                        <h2 className="section-title">Select Your Target Variable</h2>
                        <div className="target-selection">
                            {columns.map((column, index) => (
                                <div
                                    key={index}
                                    className={`target-box ${targetVariable === column ? "selected" : ""}`}
                                    onClick={() => handleSelectTarget(column)}
                                >
                                    <div className="target-box-icon"></div>
                                    <span className="target-box-text">{column}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default SupervisedLearning;
